"use client";

// Dependency-free SVG/CSS charts driven by real data. Segment colours are fixed
// hex (semantic) so they read on both light and dark themes.

export interface Segment {
  label: string;
  value: number;
  color: string;
}

export const CHART_COLORS = {
  delivered: "#22c55e",
  failed: "#ef4444",
  pending: "#f59e0b",
  brand: "#0f4c81",
  accent: "#00aeef",
  gray: "#94a3b8"
};

export function Donut({
  data,
  size = 168,
  thickness = 18,
  centervalue,
  centerlabel
}: {
  data: Segment[];
  size?: number;
  thickness?: number;
  centervalue?: string;
  centerlabel?: string;
}) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={thickness} className="stroke-gray-100" />
        {data.map((seg, i) => {
          const frac = seg.value / total;
          const dash = c * frac;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap={frac > 0 && frac < 1 ? "round" : "butt"}
              className="transition-all duration-500"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {centervalue !== undefined && <span className="text-2xl font-bold tabular-nums text-gray-900">{centervalue}</span>}
        {centerlabel && <span className="text-xs text-gray-500">{centerlabel}</span>}
      </div>
    </div>
  );
}

export function StackedBar({ data, className = "" }: { data: Segment[]; className?: string }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div className={`flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100 ${className}`}>
      {data.map((s, i) => (
        <div
          key={i}
          style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
          title={`${s.label}: ${s.value.toLocaleString()}`}
          className="h-full transition-all duration-500"
        />
      ))}
    </div>
  );
}

export function Legend({ data, className = "" }: { data: Segment[]; className?: string }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <ul className={`space-y-2 ${className}`}>
      {data.map((s, i) => (
        <li key={i} className="flex items-center justify-between gap-3 text-sm">
          <span className="flex items-center gap-2 text-gray-600">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
            {s.label}
          </span>
          <span className="flex items-center gap-2">
            <span className="font-semibold tabular-nums text-gray-900">{s.value.toLocaleString()}</span>
            <span className="w-10 text-right text-xs tabular-nums text-gray-400">
              {Math.round((s.value / total) * 100)}%
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

// Horizontal mini bar-chart (e.g. top-N list with proportional bars).
export function BarList({
  data,
  color = CHART_COLORS.brand,
  className = ""
}: {
  data: { label: string; value: number; sub?: string }[];
  color?: string;
  className?: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <ul className={`space-y-3 ${className}`}>
      {data.map((d, i) => (
        <li key={i}>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="truncate text-gray-700">{d.label}</span>
            <span className="ml-3 shrink-0 font-semibold tabular-nums text-gray-900">{d.value.toLocaleString()}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(d.value / max) * 100}%`, backgroundColor: color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
